// src/components/TechSection.jsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiNodedotjs,
  SiPhp,
  SiMysql,
  SiMongodb,
  SiLaravel,
  SiExpo,
  SiDocker,
  SiKubernetes,
  SiOpenai
} from '@icons-pack/react-simple-icons';

const categories = [
  {
    label: 'Frontend',
    items: [
      { Icon: SiReact,      name: 'React',      desc: 'Modern UI library for fast & interactive UIs.' },
      { Icon: SiNextdotjs,  name: 'Next.js',    desc: 'Full-stack React framework with SSR & SSG.' },
      { Icon: SiTypescript, name: 'TypeScript', desc: 'Statically typed JS superset for scale.' },
    ],
  },
  {
    label: 'Mobile',
    items: [
      { Icon: SiReact, name: 'React Native', desc: 'Build native iOS & Android apps with React.' },
      { Icon: SiExpo,  name: 'Expo',         desc: 'Rapid prototyping & deployment for RN.' },
    ],
  },
  {
    label: 'Backend',
    items: [
      { Icon: SiNodedotjs, name: 'Node.js',   desc: 'Event-driven JS runtime for scalable APIs.' },
      { Icon: SiPhp,       name: 'PHP',       desc: 'Battle-tested server scripting language.' },
      { Icon: SiLaravel,   name: 'Laravel',   desc: 'Elegant PHP MVC framework.' },
    ],
  },
  {
    label: 'Database',
    items: [
      { Icon: SiMysql,   name: 'MySQL',   desc: 'Reliable relational database system.' },
      { Icon: SiMongodb, name: 'MongoDB', desc: 'Flexible NoSQL document store.' },
    ],
  },
  {
    label: 'DevOps',
    items: [
      { Icon: SiDocker,     name: 'Docker',     desc: 'Containerization for reproducible builds.' },
      { Icon: SiKubernetes, name: 'Kubernetes', desc: 'Orchestrate containers at scale.' },
      { Icon: SiOpenai,     name: 'OpenAI',     desc: 'Integrate cutting-edge AI models.' },
    ],
  },
];

export default function TechSection() {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Container sx={{ py: { xs: 6, md: 10 } }}>
      <Typography
        variant="h4"
        align="center"
        fontWeight={700}
        color="primary.main"
        gutterBottom
      >
        Technology Stack
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          centered={false}
        >
          {categories.map((cat, i) => (
            <Tab key={i} label={cat.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={tabIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.4 }}
        >
          <Grid container spacing={4}>
            {categories[tabIndex].items.map(({ Icon, name, desc }, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <Card
                    elevation={4}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '&:hover': { boxShadow: 6 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        
                        p: 1.5,
                        borderRadius: '50%',
                        mb: 2,
                      }}
                    >
                      <Icon size={48} style={{ color: '#47006a' }} />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 0 }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </AnimatePresence>
    </Container>
  );
}
